import React from 'react';
import { ChevronLeft, MessageCircle, Clock, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const blogPosts = [
  {
    id: 'how-demo-process-works',
    title: 'How the Demo Process Works?',
    excerpt: 'Learn about our streamlined demo process and what to expect when trying our AI solutions.',
    date: 'February 16, 2024',
    author: 'TTRG Team',
    content: (
      <>
        <p className="mb-6">
          At TTRG, we've designed our demo process to be straightforward and valuable for your business. Here's what you can expect when you book a demo with us:
        </p>

        <h3 className="text-xl font-semibold mb-4">1. Initial Consultation (5 minutes)</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Brief introduction to your business needs</li>
          <li>Overview of your current challenges</li>
          <li>Quick assessment of which AI solutions might fit best</li>
        </ul>

        <h3 className="text-xl font-semibold mb-4">2. Solution Demonstration (20 minutes)</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Live demonstration of relevant AI solutions</li>
          <li>Customization possibilities for your business</li>
          <li>Integration examples with existing systems</li>
        </ul>

        <h3 className="text-xl font-semibold mb-4">3. Q&A Session (5 minutes)</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Detailed pricing discussion</li>
          <li>Implementation timeline</li>
          <li>Technical requirements</li>
        </ul>

        <h3 className="text-xl font-semibold mb-4">What Makes Our Demo Different?</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Focused on your specific business needs</li>
          <li>No pressure sales tactics</li>
          <li>Clear pricing and implementation plans</li>
          <li>Immediate access to technical expertise</li>
        </ul>

        <div className="bg-indigo-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-3">Ready to See It in Action?</h3>
          <p className="mb-4">Book your personalized demo today and discover how our AI solutions can transform your business.</p>
          <a 
            href="https://forms.gle/FaWzJ4QWmA3ZE8Zd8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
          >
            Schedule Your Demo
            <ArrowRight className="ml-2 w-4 h-4" />
          </a>
        </div>
      </>
    )
  }
  // More blog posts can be added here
];

export default function Blog() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700">
              <ChevronLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <a 
              href="https://wa.me/60177751631"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-green-600 hover:text-green-700"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact on WhatsApp</span>
            </a>
          </div>
        </nav>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">TTRG Blog</h1>
          <p className="text-xl text-gray-600">
            Insights, guides, and updates about AI automation for your business
          </p>
        </div>

        {blogPosts.map((post) => (
          <article key={post.id} className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{post.title}</h2>
              <div className="flex items-center text-gray-500 text-sm mb-6">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{post.date}</span>
                </div>
                <div className="mx-2">•</div>
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  <span>{post.author}</span>
                </div>
              </div>
              <div className="prose max-w-none">
                {post.content}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}